import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { toast } from '@qodo/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from 'usehooks-ts';
import { GIT_PROVIDERS } from '../../consts/git';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { useUser } from '../../hooks/useUser';
import { useWizardAnalytics } from '../../hooks/useWizardAnalytics';
import { Github } from '../../logos';
import { HOSTING_TYPES } from '../../network/httpClients/RepositoryClient/api.schema';
import { QUERY_KEYS } from '../../network/queries/consts';
import { useGitIntegrationWebhookUrlQuery } from '../../network/queries/useGitIntegrationWebhookUrlQuery';
import { QUERY_PARAMS, ROUTES } from '../../routes/consts';
import {
  DialogTitleWithIcon,
  MultiStepDialog,
} from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { getSteps } from './configuration';
import { GITHUB_ERROR_CODES } from './consts';
import { GithubInstallationContext } from './githubInstallationContext';
import {
  getCheckConnectionStepIndex,
  getGithubHostnameFromState,
  getOrganizationUrlFromOrgName,
  getStepIndexFromPath,
  isStateValid,
} from './utils';

export const GithubManifestInstallationDialog = () => {
  const [dialogKey, setDialogKey] = useState(0);
  const [organizationUrl, setOrganizationUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [installationId, setInstallationId] = useState<number>();
  const [isInstallAppSuggestionDismissed, setIsInstallAppSuggestionDismissed] =
    useState(false);
  const [isConnectionSuccessful, setIsConnectionSuccessful] = useState(false);
  const [
    githubInstallationStateFromLocalStorage,
    setGithubInstallationStateFromLocalStorage,
  ] = useLocalStorage(LOCAL_STORAGE_KEYS.GITHUB_INSTALLATION_STATE, undefined);
  // Track installation state for direct routing past first step
  const initialInstallationStateRef = useRef(
    githubInstallationStateFromLocalStorage,
  );
  // Track if error was already handled to prevent infinite loops
  const errorHandledRef = useRef(false);
  // Track intentional wizard resets so onCancel ignores the Radix onOpenChange(false)
  // that fires when the old dialog unmounts due to a key change
  const isResettingToStep1Ref = useRef(false);

  const {
    hasGithubCloudWizardAccess,
    hasGithubServerWizardAccess,
    isGithubServerWizardLoading,
    isGithubCloudWizardLoading,
  } = useUser();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const hostingType = searchParams.get(QUERY_PARAMS.HOSTING_TYPE);
  const isSelfHosted = hostingType === HOSTING_TYPES.SELF_HOSTED;
  const hasAccessForHostingType = isSelfHosted
    ? hasGithubServerWizardAccess
    : hasGithubCloudWizardAccess;
  const areGithubWizardFeatureFlagsLoading =
    isGithubCloudWizardLoading || isGithubServerWizardLoading;

  const titleKey = isSelfHosted ? 'title.server' : 'title.cloud';
  const spawnGeneral = spawn('dialogs.github-installation.general');
  const spawnErrors = spawn('dialogs.github-installation.errors');
  const checkConnectionStepIndex = getCheckConnectionStepIndex();

  const steps = getSteps();
  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.GITHUB,
    wizardVersion: 'v1',
    steps,
  });

  const { data: webhookUrl, isLoading: isWebhookUrlLoading } =
    useGitIntegrationWebhookUrlQuery(GIT_PROVIDERS.GITHUB);

  // remove the state from local storage immediately as we have already stored it as default value in githubInstallationStateFromLocalStorage
  useEffect(() => {
    if (githubInstallationStateFromLocalStorage) {
      setGithubInstallationStateFromLocalStorage(undefined);
    }
  }, [
    githubInstallationStateFromLocalStorage,
    setGithubInstallationStateFromLocalStorage,
  ]);

  useEffect(() => {
    const slug = searchParams.get('slug');

    if (slug) {
      setSlug(slug);
    }

    const orgName = searchParams.get('org_name');

    if (orgName) {
      const githubHostname = getGithubHostnameFromState(
        initialInstallationStateRef.current,
      );

      setOrganizationUrl(
        getOrganizationUrlFromOrgName(orgName, githubHostname),
      );
    }

    const installationIdFromSearchParams =
      Number(searchParams.get('installation_id')) || undefined;

    if (installationIdFromSearchParams) {
      setInstallationId(installationIdFromSearchParams);
    }
  }, [searchParams]);

  // This effect is the gatekeeper for the navigation logic
  useEffect(() => {
    const stepIndex = getStepIndexFromPath(location.pathname);
    const errorCode = searchParams.get(QUERY_PARAMS.ERROR_CODE);
    const isValidErrorCode =
      errorCode && Object.values(GITHUB_ERROR_CODES).includes(errorCode as any);
    const shouldDisplayErrorToasts =
      isValidErrorCode && !errorHandledRef.current;

    if (shouldDisplayErrorToasts) {
      toast.error(spawnErrors(errorCode));
      errorHandledRef.current = true;
      initialInstallationStateRef.current = undefined;

      goToStep1();

      return;
    }

    // Handle installation state validation and redirects
    // Only redirect if we're on check connection step or later
    const isCheckConnectionStepOrLater =
      stepIndex !== undefined && stepIndex >= checkConnectionStepIndex;

    if (isCheckConnectionStepOrLater) {
      const hasInstallationState = !!initialInstallationStateRef.current;
      const isCheckConnectionStep = stepIndex === checkConnectionStepIndex;

      // Allow staying if:
      // 1. Connection already successful
      // 2. On check connection step with valid OAuth state
      // 3. On later steps with installation state (already passed check connection)
      const canStay =
        isConnectionSuccessful ||
        (hasInstallationState &&
          isCheckConnectionStep &&
          isStateValid(initialInstallationStateRef.current)) ||
        (hasInstallationState && !isCheckConnectionStep);

      if (!canStay) {
        goToStep1();
      }
    }
  }, [
    searchParams,
    location.pathname,
    isConnectionSuccessful,
    navigate,
    spawnErrors,
  ]);

  const goToStep1 = () => {
    isResettingToStep1Ref.current = true;
    // increment dialog key to reset wizard
    setDialogKey((prev) => prev + 1);

    const hostingTypeParam = searchParams.get(QUERY_PARAMS.HOSTING_TYPE);
    const preservedSearch = hostingTypeParam
      ? `?${QUERY_PARAMS.HOSTING_TYPE}=${hostingTypeParam}`
      : '';

    navigate(`${ROUTES.GITHUB_INSTALLATION_STEP_1}${preservedSearch}`, {
      replace: true,
    });
  };

  const close = () => {
    const qs = new URLSearchParams(
      [...searchParams].filter(([key]) => key === QUERY_PARAMS.HOSTING_TYPE),
    ).toString();
    const search = qs ? `?${qs}` : '';

    navigate({ pathname: ROUTES.INTEGRATIONS, search });
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    if (isResettingToStep1Ref.current) {
      isResettingToStep1Ref.current = false;

      return;
    }

    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
    });
  };

  if (!hasAccessForHostingType && !areGithubWizardFeatureFlagsLoading) {
    console.warn("You're not allowed to access this page");

    return <Navigate to={ROUTES.ROOT} />;
  }

  return (
    <GithubInstallationContext.Provider
      value={{
        organizationUrl,
        setOrganizationUrl,
        slug,
        installationId,
        isInstallAppSuggestionDismissed,
        setIsInstallAppSuggestionDismissed,
        isConnectionSuccessful,
        setIsConnectionSuccessful,
        webhookUrl,
        isWebhookUrlLoading,
      }}
    >
      <MultiStepDialog
        key={dialogKey}
        title={
          <DialogTitleWithIcon text={spawnGeneral(titleKey)}>
            <Github size={24} />
          </DialogTitleWithIcon>
        }
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        className="sm:max-w-250!"
        disableOutsideClick={true}
        disableEscapeKey={true}
        defaultEarliestStepIndexAvailable={
          initialInstallationStateRef.current ? checkConnectionStepIndex : 0
        }
        defaultFurthestStepIndex={
          initialInstallationStateRef.current ? checkConnectionStepIndex : 0
        }
      />
    </GithubInstallationContext.Provider>
  );
};
